/**
 * M3X.WORKSPACE-CLOSURE FINAL PR 5 — restore tab made unmissable.
 *
 * 5 hotfix rounds and Amer still barely saw it. Per FINAL §5:
 *   - Width 20→28px default · 24→36px hover
 *   - Height 64→96px
 *   - Box-shadow 2px 0 12px → 4px 0 20px rgba(34,96,201,0.4)
 *   - Icon 14→18px
 *   - RAIL label below icon (uppercase 8px white letter-spaced)
 *   - Pulse for first 10 sessions (was 5)
 *
 * Plus: a separate persistent "Rail hidden · press [" hint label
 * at the top of the content area when rail2State === 'hidden',
 * shown for the first 3 visits, fading after 5s.
 */

import { ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";


export const RESTORE_TAB_PULSE_MAX = 10;
const _HIDDEN_LABEL_MAX = 3;


function _pulseCountKey(userId: string | null): string {
  return `solvait.workspace.restoreTabPulseCount.${userId ?? "anon"}`;
}


function _hiddenLabelKey(userId: string | null): string {
  return `solvait.workspace.hiddenLabelCount.${userId ?? "anon"}`;
}


export interface Rail2RestoreTabProps {
  /** Render only when Rail 2 is hidden. */
  show: boolean;
  /** Restore Rail 2 to Expanded state. */
  onRestore: () => void;
  /** Per-user pulse counter persistence. */
  userId?: string | null;
}


export function Rail2RestoreTab({
  show,
  onRestore,
  userId = null,
}: Rail2RestoreTabProps) {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(false);

  // Pulse for the first 10 sessions
  const [shouldPulse, setShouldPulse] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      const current = parseInt(
        window.localStorage.getItem(_pulseCountKey(userId)) ?? "0",
        10,
      );
      if (current < RESTORE_TAB_PULSE_MAX) {
        window.localStorage.setItem(
          _pulseCountKey(userId),
          String(current + 1),
        );
        return true;
      }
      return false;
    } catch {
      return false;
    }
  });

  // Persistent "Rail hidden" label for the first 3 visits
  const [showHiddenLabel, setShowHiddenLabel] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    try {
      const current = parseInt(
        window.localStorage.getItem(_hiddenLabelKey(userId)) ?? "0",
        10,
      );
      if (current < _HIDDEN_LABEL_MAX) {
        window.localStorage.setItem(
          _hiddenLabelKey(userId),
          String(current + 1),
        );
        return true;
      }
      return false;
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!shouldPulse) return;
    // Pulse for 8 seconds then stop
    const t = setTimeout(() => setShouldPulse(false), 8_000);
    return () => clearTimeout(t);
  }, [shouldPulse]);

  useEffect(() => {
    if (!showHiddenLabel) return;
    const t = setTimeout(() => setShowHiddenLabel(false), 5_000);
    return () => clearTimeout(t);
  }, [showHiddenLabel]);

  if (!show) return null;

  return (
    <>
      <div className="relative flex">
        <button
          type="button"
          onClick={onRestore}
          data-rail2-restore-tab
          aria-label={t(
            "workspace.rail2.expand.aria",
            "Expand sub-domains",
          )}
          className={[
            "group/restore-tab",
            "flex flex-shrink-0 cursor-pointer flex-col items-center justify-center self-stretch",
            "transition-[width] duration-150 ease-out",
            // FINAL PR 5 — 28px default → 36px hover (was 20 → 24)
            "w-7 hover:w-9",
            "focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-white",
            shouldPulse ? "animate-[restoreTabPulse_2s_ease-in-out_4]" : "",
          ].join(" ")}
          style={{
            background: hovered
              ? "linear-gradient(180deg, #00C2B4 0%, #1F58BC 50%, #9A40B2 100%)"
              : "linear-gradient(180deg, #00D3C2 0%, #2260C9 50%, #A846BF 100%)",
            // Outer rounded, inner flush
            borderEndEndRadius: "6px",
            borderStartEndRadius: "6px",
            // FINAL PR 5 — stronger glow shadow + larger min-height
            boxShadow: "4px 0 20px rgba(34, 96, 201, 0.4)",
            minHeight: "96px",
            color: "white",
            gap: "2px",
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          <ChevronRight
            className="rtl:rotate-180"
            style={{ width: "18px", height: "18px" }}
            aria-hidden
          />
          {/* FINAL PR 5 — uppercase 8px white RAIL label below the icon */}
          <span
            aria-hidden
            style={{
              fontSize: "8px",
              color: "white",
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              fontWeight: 600,
            }}
          >
            {t("workspace.rail2.expand.rail", "RAIL")}
          </span>
        </button>
        {hovered && (
          <span
            role="tooltip"
            data-rail2-restore-tooltip
            className="pointer-events-none absolute whitespace-nowrap"
            style={{
              top: "50%",
              insetInlineStart: "calc(100% + 8px)",
              transform: "translateY(-50%)",
              backgroundColor: "#1e293b",
              color: "white",
              fontSize: "11px",
              padding: "6px 10px",
              borderRadius: "6px",
              zIndex: 30,
            }}
          >
            {t(
              "workspace.rail2.expand.tooltip",
              "Show rail · or press [",
            )}
          </span>
        )}
      </div>

      {/* FINAL PR 5 — persistent "Rail hidden · press [" hint at the
          start-edge of the content area. Auto-fades after 5s; only
          shows on the first 3 visits per user. */}
      {showHiddenLabel && (
        <div
          data-rail2-hidden-label
          aria-live="polite"
          className="pointer-events-none fixed transition-opacity duration-500"
          style={{
            top: "72px",
            insetInlineStart: "112px",
            fontSize: "11px",
            color: "#94a3b8",
            zIndex: 20,
          }}
        >
          {t(
            "workspace.rail2.expand.hiddenHint",
            "Rail hidden · press [ or click chevron →",
          )}
        </div>
      )}
    </>
  );
}


export default Rail2RestoreTab;
